import React, {useCallback, useState} from "react";
import {useAtom} from "jotai";
import {Button, ListItemIcon, Menu, MenuItem, Stack, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {useFields} from "../../hooks/fieldsHooks";
import {CompanyFilter} from "../CompanyFilter";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {FarmsTable} from "../farms/FarmsTable";
import {FieldCreateDialog} from "../dialogs/FieldCreateDialog";
import {selectedCompanyAtom} from "../../store/filterSelectionStore";
import {invalidateAllCompanyFarmsHooks, useCompanyFarms} from "../../hooks/farmsHooks";
import {useTranslation} from "react-i18next";
import {queryClient} from "../../utils/queryClient";

export const AdminFarmsView: React.FC = () => {
    const {t} = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [farmCreateDialogOpened, setFarmCreateDialogOpened] = useState<boolean>(false);
    const [fieldCreateDialogOpened, setFieldCreateDialogOpened] = useState<boolean>(false);
    const [selectedCompany] = useAtom(selectedCompanyAtom);
    const companyId = selectedCompany ? selectedCompany.id : undefined;
    const farms = useCompanyFarms(companyId);
    const fields = useFields({companyId, orderId: undefined, farmId: undefined});

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const refreshAll = useCallback(() => {
        if (companyId) {
            invalidateAllCompanyFarmsHooks(companyId);
            void queryClient.invalidateQueries({
                queryKey: ["fields", {companyId}],
            });
        }
    }, [companyId]);

    const onFarmCreateDialogClose = (isSuccessful: boolean) => {
        setFarmCreateDialogOpened(false);
        if (isSuccessful) {
            void refreshAll();
        }
        setAnchorEl(null);
    };

    const onFieldCreateDialogClose = (isSuccessful: boolean) => {
        setFieldCreateDialogOpened(false);
        if (isSuccessful) {
            void refreshAll();
        }
        setAnchorEl(null);
    };

    return (
        <Stack paddingX="1.5rem" paddingTop="1.5rem" paddingBottom="0.5rem">
            <Stack direction="column">
                <CompanyFilter></CompanyFilter>
                <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                    <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                        Farms
                    </Typography>
                    <div>
                        <Button startIcon={<AddIcon />} onClick={handleClick} endIcon={<ArrowDownIcon />}>
                            Create New
                        </Button>
                        <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                "aria-labelledby": "basic-button",
                            }}
                        >
                            <MenuItem onClick={() => setFarmCreateDialogOpened(true)}>
                                <ListItemIcon>
                                    <AddIcon />
                                </ListItemIcon>
                                New Farm
                            </MenuItem>
                            <MenuItem onClick={() => setFieldCreateDialogOpened(true)}>
                                <ListItemIcon>
                                    <AddIcon />
                                </ListItemIcon>
                                {t("companies.farms_view.new_field")}
                            </MenuItem>
                        </Menu>
                    </div>
                </Stack>

                {companyId && (
                    <FarmsTable
                        farms={farms?.data?.farms ?? []}
                        refreshFarms={refreshAll}
                        companyId={companyId}
                        refreshFields={refreshAll}
                        fields={fields.isSuccess ? fields.data : []}
                    />
                )}
            </Stack>

            {farmCreateDialogOpened && companyId && (
                <FarmCreateDialog
                    mode="new"
                    companyId={companyId}
                    open={farmCreateDialogOpened}
                    onClose={onFarmCreateDialogClose}
                />
            )}

            {fieldCreateDialogOpened && companyId && (
                <FieldCreateDialog
                    companyId={companyId}
                    open={fieldCreateDialogOpened}
                    mode="new"
                    onClose={onFieldCreateDialogClose}
                />
            )}
        </Stack>
    );
};
